/**
 * Remix Video Selector Modal
 * Allows users to select a completed video to remix
 */

import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { Video } from "@/features/studio-ai/types/studio-ai.types"

import { getRemixableVideos } from "@/api/studio-ai-api"
import { Check, Film } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Spinner } from "@/components/ui/spinner"
import { cn } from "@/lib/utils"

type RemixVideoSelectorModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (video: Video) => void
}

export function RemixVideoSelectorModal({
  open,
  onOpenChange,
  onSelect,
}: RemixVideoSelectorModalProps) {
  const { t } = useTranslation("translation", { keyPrefix: "studioAi.remixSelectorModal" })
  const [videos, setVideos] = useState<Video[]>([])
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (open) {
      void loadVideos()
    }
  }, [open])

  const loadVideos = async () => {
    setIsLoading(true)
    try {
      const remixableVideos = await getRemixableVideos()

      setVideos(remixableVideos)

      if (remixableVideos.length === 0) {
        toast.info(t("noCompletedVideosToast"), {
          description: t("noCompletedVideosToastDesc"),
        })
      }
    } catch {
      toast.error(t("failedToLoadVideos"), {
        description: t("failedToLoadVideosDesc"),
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSelect = () => {
    if (selectedVideo) {
      onSelect(selectedVideo)
      onOpenChange(false)
      setSelectedVideo(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] overflow-hidden sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("title")}</DialogTitle>
          <DialogDescription>{t("description")}</DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[calc(90vh-200px)] pr-4">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Spinner className="text-muted-foreground h-8 w-8" />
            </div>
          )}

          {!isLoading && videos.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Film className="text-muted-foreground mb-4 h-12 w-12" />
              <p className="text-muted-foreground text-sm">{t("noVideosAvailable")}</p>
              <p className="text-muted-foreground mt-1 text-xs">{t("completeVideoFirst")}</p>
            </div>
          )}

          {!isLoading && videos.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              {videos.map((video) => (
                <button
                  key={video.id}
                  className={cn(
                    "focus-visible:ring-ring relative overflow-hidden rounded-lg border-2 transition-[border-color,box-shadow] hover:shadow-lg focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset",
                    selectedVideo?.id === video.id
                      ? "border-primary ring-primary ring-2 ring-offset-2"
                      : "border-border hover:border-primary/50"
                  )}
                  onClick={() => setSelectedVideo(video)}
                >
                  {/* Thumbnail */}
                  <div className="bg-muted aspect-video">
                    {video.thumbnail_url ? (
                      <img
                        alt={video.prompt}
                        className="h-full w-full object-cover"
                        src={video.thumbnail_url}
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center">
                        <Film className="text-muted-foreground h-12 w-12" />
                      </div>
                    )}
                  </div>

                  {/* Selection indicator */}
                  {selectedVideo?.id === video.id && (
                    <div className="border-foreground bg-primary absolute top-2 right-2 rounded-full border p-1">
                      <Check className="text-primary-foreground h-4 w-4" />
                    </div>
                  )}

                  {/* Video info */}
                  <div className="bg-card p-3 text-left">
                    <p className="line-clamp-2 text-sm font-medium">{video.prompt}</p>
                    <div className="text-muted-foreground mt-1 flex items-center gap-2 text-xs">
                      <span>{video.duration}s</span>
                      <span>•</span>
                      <span>{video.aspect_ratio}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("cancel")}
          </Button>
          <Button disabled={!selectedVideo} onClick={handleSelect}>
            {t("selectVideo")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
